/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { getLanguageMatch, getUserLanguageCode, languageCodeToName } from 'js/lib/language';
import { getLabelForAutotranslation } from 'js/lib/languageLocalized';
import { isAuthenticatedUser } from 'js/lib/user';

import { Typography2, typography2Color } from '@coursera/cds-core';
import { ClosedCaptioningIcon } from '@coursera/cds-icons';
import type { ButtonName, SectionName } from '@coursera/event-pulse-types';
import { Button } from '@coursera/event-pulse/react';

import { getHeadingStyle } from 'bundles/unified-description-page-common/components/modules/product-overview/details/Details';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useLanguageInfoDialog } from 'bundles/unified-description-page-common/contexts/LanguageInfoDialogContext';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import type { Languages } from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import { getAudienceLanguageCode } from 'bundles/unified-description-page-common/utils/dubbingUtil';
import getLanguagesMetadata from 'bundles/unified-description-page-common/utils/getLanguagesMetadata';

import _t from 'i18n!nls/xdp';

type Props = {
  languages: Languages;
};

const styles = {
  itemTitle: css`
    padding: var(--cds-spacing-100) 0;
  `,
  icon: css`
    display: block;
  `,
  button: css`
    padding: 0;
    text-align: left;

    /* set vertical-align to unset to vertically center the button within the "Subtitles" line */
    vertical-align: unset;

    :hover {
      background: transparent;
    }
  `,
  buttonGray: css`
    ${typography2Color.supportText};
    font-weight: normal;
    text-decoration: underline;

    &:hover {
      ${typography2Color.supportText};
      text-decoration: none;
    }
  `,
  noSubtitle: css`
    padding: var(--cds-spacing-150) 0;
  `,
};

const LanguagesDetailItem = ({ languages }: Props) => {
  const { openDialog } = useLanguageInfoDialog();
  const userLanguage = isAuthenticatedUser() ? getUserLanguageCode() : _t.getLocale();
  const eventingProductData = useEventingProductData();
  const { marketingSource } = usePageInfo();
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;

  const trackingData = {
    button: { name: 'description_page_available_languages' as ButtonName },
    pageSection: {
      sectionName: 'about' as SectionName,
    },
    ...eventingProductData,
  };

  if (!languages.primaryLanguages?.length) {
    return null;
  }

  const { autoTranslations, humanTranslations, subtitles, originalLanguage, dubbedLanguages } =
    getLanguagesMetadata(languages);

  const userLanguageForAiDubbing = getAudienceLanguageCode(languages.dubbedLanguages);
  const enableAiDubbingOnXdp = !marketingSource && !!userLanguageForAiDubbing && dubbedLanguages.length > 0;

  // combine "available languages" and "video subtitles" into one section when XDP has AI dubbing languages
  const totalTranslations =
    dubbedLanguages.length > 0
      ? Array.from(new Set([...autoTranslations, ...humanTranslations, ...subtitles, ...dubbedLanguages])).length
      : autoTranslations.length + humanTranslations.length;

  let languageInfoLink: React.ReactNode = null;

  if (totalTranslations > 0) {
    const userLanguageAutoMatch =
      !!userLanguage && autoTranslations.find((language) => getLanguageMatch(userLanguage, language));
    const userLanguageHumanMatch =
      !!userLanguage && humanTranslations.find((language) => getLanguageMatch(userLanguage, language));
    let linkMessage: string;
    let matchedLanguageName = '';

    if (!enableAiDubbingOnXdp && (userLanguageAutoMatch || userLanguageHumanMatch)) {
      linkMessage = _t(
        '{languageCount, plural, =1 {# language available} other {# languages available}}, including {languageName}'
      );
      if (userLanguageHumanMatch) {
        matchedLanguageName = languageCodeToName(userLanguageHumanMatch);
      } else if (userLanguageAutoMatch) {
        matchedLanguageName = `${languageCodeToName(userLanguageAutoMatch)} (${getLabelForAutotranslation(
          userLanguageAutoMatch
        )})`;
      }
    } else {
      linkMessage = _t('{languageCount, plural, =1 {# language available} other {# languages available}}');
    }

    languageInfoLink = (
      <Button
        variant="ghost"
        size="small"
        onClick={openDialog}
        css={[styles.button, enableXDPRedesignPhase1 ? styles.buttonGray : undefined]}
        trackingData={trackingData}
        noViewTracking
      >
        <FormattedMessage
          message={linkMessage}
          languageCount={totalTranslations + 1}
          languageName={matchedLanguageName}
        />
      </Button>
    );
  } else if (subtitles.length > 0) {
    languageInfoLink = (
      <Button
        variant="ghost"
        size="small"
        onClick={openDialog}
        css={styles.button}
        trackingData={trackingData}
        noViewTracking
      >
        {_t('Video subtitles available')}
      </Button>
    );
  }

  return (
    <React.Fragment>
      <ClosedCaptioningIcon size="large" css={styles.icon} />
      <Typography2
        variant={getHeadingStyle(enableXDPRedesign)}
        css={languageInfoLink ? styles.itemTitle : styles.noSubtitle}
        component="div"
      >
        {enableAiDubbingOnXdp ? (
          <FormattedMessage
            message={_t('Taught in {language} (AI Dubbing)')}
            language={languageCodeToName(userLanguageForAiDubbing)}
          />
        ) : (
          // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          <FormattedMessage message={_t('Taught in {language}')} language={languageCodeToName(originalLanguage)} />
        )}
      </Typography2>
      {languageInfoLink && (
        <Typography2 variant="bodySecondary" color="supportText" component="div">
          {languageInfoLink}
        </Typography2>
      )}
    </React.Fragment>
  );
};

export default LanguagesDetailItem;
